import {
  createCompanyMarketApp,
  deleteCompanyMarketApp,
  createAccount,
  recreateLink,
  validateAccountRegistered,
  getCompanies,
  getCompanyMarketplaceApps,
  getCompanyMarketplaceAppsByCompanyId,
} from "@/dispatchers/marketAppDispacher";
import {
  nhsAhtWeightTemplateId,
  nhsAhtWeightServiceId,
  nhsAhtTravelHealthTemplateId,
  nhsAhtTravelHealthServiceId,
} from "@/data/constantStrings";
import { NHSServiceNames } from "@/components/nhs-templates/NHSServicesHelper";

// Maps a PGD to the single NHS service it enables on the CompanyMarketApp record.
export const pgdEnabledService: Record<
  string,
  { serviceId: string; serviceName: string }
> = {
  "weight-loss-pgd": {
    serviceId: nhsAhtWeightServiceId,
    serviceName: NHSServiceNames[nhsAhtWeightTemplateId],
  },
  "travel-pgd": {
    serviceId: nhsAhtTravelHealthServiceId,
    serviceName: NHSServiceNames[nhsAhtTravelHealthTemplateId],
  },
};

// Reverse lookup used to work out which PGD is installed from the
// EnabledServices returned by the API, since PayLoad.pgds can drift out of sync.
export function getPgdIdForServiceId(serviceId: string): string | undefined {
  return Object.keys(pgdEnabledService).find(
    (pgdId) => pgdEnabledService[pgdId].serviceId === serviceId,
  );
}

export interface AlliedHealthPgd {
  id: string;
  title: string;
  description: string;
  // Charged per completed consultation at the site.
  pricePerTransaction: number;
  // Maximum charge per month per site, regardless of transaction volume.
  monthlyCapPerSite: number;
  vat: boolean;
  category: string;
  url: string;
  features?: string[];
}

export const alliedHealthPgds: AlliedHealthPgd[] = [
  {
    id: "weight-loss-pgd",
    title: "Weight Management PGD",
    description:
      "Ready-to-adopt Patient Group Direction template for weight management services, maintained by Allied Health Training. Installed per site and charged per consultation.",
    pricePerTransaction: 3,
    monthlyCapPerSite: 60,
    vat: true,
    category: "Weight Management",
    url: "https://ahtraining.co.uk",
    features: [
      "Ready-to-use PGD template",
      "Kept up to date by Allied Health Training",
      "£3 per consultation, capped at £60 per month per site",
      "No consultations in a month means no charge",
      "Charged in addition to your standard Titan consultation fee",
    ],
  },
  {
    id: "travel-pgd",
    title: "Travel Consultations PGD",
    description:
      "Ready-to-adopt Patient Group Direction template for travel health services, maintained by Allied Health Training. Installed per site and charged per consultation.",
    pricePerTransaction: 4,
    monthlyCapPerSite: 80,
    vat: true,
    category: "Travel Health",
    url: "https://ahtraining.co.uk",
    features: [
      "Ready-to-use PGD template",
      "Kept up to date by Allied Health Training",
      "£4 per consultation, capped at £80 per month per site",
      "No consultations in a month means no charge",
      "Charged in addition to your standard Titan consultation fee",
    ],
  },
];

export const pgdCategories = ["All PGDs", "Weight Management", "Travel Health"];

// End-of-month charge for a single site: transactions are billed at the
// per-transaction price, up to the site's monthly cap.
export function monthlyChargeForSite(
  pgd: AlliedHealthPgd,
  transactions: number,
): number {
  if (transactions <= 0) return 0;
  return Math.min(
    transactions * pgd.pricePerTransaction,
    pgd.monthlyCapPerSite,
  );
}

// Transactions at which a site reaches its monthly cap.
export function transactionsToReachCap(pgd: AlliedHealthPgd): number {
  return Math.ceil(pgd.monthlyCapPerSite / pgd.pricePerTransaction);
}

export interface InstalledPgdRecord {
  pgdId: string;
  installedAt: string;
  pricePerTransaction: number;
  monthlyCapPerSite: number;
}

// Persists the current set of PGDs installed at a site as a single
// CompanyMarketApp record (POST /MarketApp/create-companymarketapp).
// The PayLoad carries the full list of installed PGDs for that site, so
// calling this again with an updated list overwrites the previous record.
// EnabledServices mirrors pgdRecords, one entry per currently installed PGD.
export const handleAlliedHealthTrainingSave = async (
  companyId: string,
  app: { id: string; name: string; marketType: number },
  location: { ods?: string } | undefined,
  pgdRecords: InstalledPgdRecord[],
  email?: string | null,
) => {
  const enabledServices = pgdRecords
    .map((record) => pgdEnabledService[record.pgdId])
    .filter(
      (service): service is { serviceId: string; serviceName: string } =>
        !!service,
    )
    .map((service) => ({
      serviceId: service.serviceId,
      serviceName: service.serviceName,
    }));
  const companyMarketApp = {
    companyID: companyId,
    ods: location?.ods || "",
    marketAppID: app.id,
    marketAppName: app.name,
    marketType: app.marketType,
    enabledServices: enabledServices,
    payLoad: null,
    email: email || null,
    isAccountRegistered: true,
    isActive: true,
  };

  return createCompanyMarketApp(companyMarketApp);
};
